import { useCallback, useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// components
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

// schema
import { termoSchema } from "@pages/client/details-model/components/modal-envio-excel/schema";

import { useStoreZustandModelosCliente } from "@/store-zustand/cliente/details-modelo";

import { limparDocumento } from "@/utils/format-document";

const TermModal = ({
  handleSubmitFile,
  handleCloseModal,
}: {
  handleSubmitFile: () => void;
  handleCloseModal: () => void;
}) => {
  const { dadosResponsabilidade, setDadosResponsabilidade } = useStoreZustandModelosCliente();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // definindo o tipo do formulário baseado no schema
  type FormData = z.infer<typeof termoSchema>;

  // inicializando o react-hook-form com o resolver do Zod
  const {
    register,
    handleSubmit,
    // setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(termoSchema) });

  // CPF Mask
  const formatCPF = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .slice(0, 14);
  };

  const closeModal = useCallback(() => {
    handleCloseModal();
    setTimeout(() => {
      reset();
      setIsSubmitting(false);
    }, 300);
  }, [handleCloseModal, reset]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    setDadosResponsabilidade({
      ...dadosResponsabilidade,
      nome: data.nomeCompleto,
      documento: limparDocumento(data.cpf),
    });

    handleSubmitFile();
    setIsSubmitting(false);
    closeModal();
    return data;
  };

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeModal]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      {/* Modal */}

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Modal Content */}
        <div className="animate-in zoom-in-95 relative max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl duration-300">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-primary to-purple-600 px-6 py-3 text-white">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Termo de Responsabilidade</h2>
              <button
                onClick={closeModal}
                className="rounded-full p-1 transition-colors hover:bg-white/20"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[calc(85vh-56px)] space-y-3 overflow-hidden p-5 text-sm">
            {/* Terms */}
            <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-purple-50 p-3">
              <div className="space-y-2 text-xs text-gray-700">
                <div className="leading-relaxed">
                  <span className="font-bold text-blue-600">1ª Cláusula:</span> Todos os dados
                  enviados são de responsabilidade do cliente, tal como qualquer incorreção dos
                  dados, serão produzidos sem responsabilidade da Let 3com.
                </div>
                <div className="leading-relaxed">
                  <span className="font-bold text-blue-600">2ª Cláusula:</span> Todas as fotos
                  enviadas são de responsabilidade do cliente, devendo ser enviadas com a resolução
                  mínima de 300dpi, foto proporcional e com tamanho mínimo de 225px x 300px,
                  juntamente com contraste, brilho e enquadramento corretos. A Let'scom não trata
                  nem altera as fotos.
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div className="text-center">
                <h3 className="mb-1 text-base font-bold text-gray-800">Seus Dados</h3>
                <p className="text-xs text-gray-600">
                  Preencha as informações abaixo para continuar
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    {...register("nomeCompleto")}
                    placeholder="Digite seu nome completo"
                    className={`w-full rounded-lg border-2 px-3 py-2 text-sm outline-none transition-all duration-300 ${
                      errors.nomeCompleto
                        ? "border-red-300 bg-red-50 focus:border-red-500"
                        : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
                    }`}
                  />
                  {errors.nomeCompleto && (
                    <p className="mt-1 text-xs text-red-500">{errors.nomeCompleto.message}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">CPF *</label>
                  <input
                    type="text"
                    {...register("cpf", {
                      onChange: (e) => {
                        const formatted = formatCPF(e.target.value);
                        e.target.value = formatted;
                        return formatted;
                      },
                    })}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className={`w-full rounded-lg border-2 px-3 py-2 text-sm outline-none transition-all duration-300 ${
                      errors.cpf
                        ? "border-red-300 bg-red-50 focus:border-red-500"
                        : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
                    }`}
                  />
                  {errors.cpf && <p className="mt-1 text-xs text-red-500">{errors.cpf.message}</p>}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border-2 border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  disabled={isSubmitting}
                  className="rounded bg-primary px-4 py-2 text-white"
                  type="submit"
                >
                  {isSubmitting ? <SpinnerSimple /> : "Concordar e Enviar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TermModal };
